/** 设置往返的序列化边界；只保存本地工作，不代替服务端事实。 */
import { validQuoteJourneySession } from "./agentJourneyView";
import { quoteProjectPath } from "@/modules/quoteproject/quoteProjectView";
import { QUOTE_SETTINGS_PATHS } from "@/modules/quickquote/settings/quoteSettingsPaths";

export const JOURNEY_TTL_MS = 2 * 60 * 60 * 1000;
export const JOURNEY_JSON_LIMIT = 2 * 1024 * 1024;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PAGE_KEYS = [
  "quickquote",
  "wizard",
  "frompart",
  "rfq",
  "worksheet",
  "legacy",
  "project",
  "agent",
] as const;
export type JourneyPageKey = (typeof PAGE_KEYS)[number];
export type JourneyJson =
  | null
  | boolean
  | number
  | string
  | JourneyJson[]
  | { [key: string]: JourneyJson };
export type JourneyPage = {
  key: JourneyPageKey;
  version: 1;
  data: Record<string, JourneyJson>;
  files: Array<{ slot: string; file: File }>;
};
export type JourneySnapshot = {
  version: 1;
  id: string;
  scope: string;
  createdAt: number;
  origin: string;
  target: string;
  pages: JourneyPage[];
};

export function quoteJourneyScope(tenant: string, user: string): string | null {
  return tenant.trim() && user.trim() ? JSON.stringify([tenant, user]) : null;
}

function localUrl(href: unknown): URL | null {
  if (
    typeof href !== "string" ||
    href.length > 2048 ||
    !href.startsWith("/") ||
    href.startsWith("//") ||
    /[\\\s]/u.test(href) ||
    [...href].some((char) => {
      const code = char.charCodeAt(0);
      return code < 32 || code === 127;
    })
  )
    return null;
  try {
    const url = new URL(href, "https://quote.invalid");
    const rawPath = href.split(/[?#]/, 1)[0];
    // URL会折叠..等路径；必须和输入相等且无编码路径才接受。
    if (
      url.origin !== "https://quote.invalid" ||
      rawPath !== url.pathname ||
      rawPath.includes("%")
    )
      return null;
    if (href.includes("#") && !/^#[a-z][a-z0-9_-]{0,127}$/i.test(url.hash))
      return null;
    return url;
  } catch {
    return null;
  }
}

export function safeQuoteOrigin(href: unknown): string | null {
  const url = localUrl(href);
  if (!url) return null;
  const seen = new Set<string>();
  for (const [key, value] of url.searchParams) {
    if (seen.has(key)) return null;
    seen.add(key);
    if (url.pathname === "/app/agent") {
      if (
        !(
          (key === "s" && validQuoteJourneySession(value)) ||
          (key === "q" && value.length <= 4000)
        )
      )
        return null;
    } else if (url.pathname === "/app/quickquote") {
      if (
        !["part_id", "quote_id", "draft_id"].includes(key) ||
        !UUID.test(value)
      )
        return null;
    } else if (url.pathname === "/cnc/quotes") {
      if (key !== "tab" || !["new", "history", "rate-cards"].includes(value))
        return null;
    } else if (
      /^\/(?:cnc\/quote-projects|app\/quickquote\/projects)\//.test(
        url.pathname,
      )
    ) {
      if (
        key !== "step" ||
        !["checklist", "workbench", "summary"].includes(value)
      )
        return null;
    } else return null;
  }
  if (
    url.pathname !== "/app/agent" &&
    url.pathname !== "/app/quickquote" &&
    url.pathname !== "/cnc/quotes" &&
    !/^\/(?:cnc\/quote-projects|app\/quickquote\/projects)\/[0-9a-f-]+$/i.test(
      url.pathname,
    )
  )
    return null;
  if (
    /^\/(?:cnc\/quote-projects|app\/quickquote\/projects)\//.test(
      url.pathname,
    ) &&
    !UUID.test(url.pathname.split("/").at(-1) ?? "")
  )
    return null;
  if (
    url.pathname === "/app/agent" &&
    !validQuoteJourneySession(url.searchParams.get("s"))
  )
    return null;
  return href as string;
}

/** 只规范化已经通过原来源白名单的旧项目路径；原查询/hash逐字保留。 */
export function canonicalQuoteOrigin(href: unknown): string | null {
  const safe = safeQuoteOrigin(href);
  return (
    safe?.replace(
      /^\/cnc\/quote-projects\/([0-9a-f-]+)/i,
      (_prefix, id: string) => quoteProjectPath(id),
    ) ?? null
  );
}

export function safeSettingsJourneyTarget(href: unknown): string | null {
  const url = localUrl(href);
  if (!url || url.search) return null;
  const anchors: Record<string, string[]> = {
    [QUOTE_SETTINGS_PATHS.rates]: ["", "#machine_rates"],
    [QUOTE_SETTINGS_PATHS.policy]: ["", "#tax", "#lead"],
    [QUOTE_SETTINGS_PATHS.catalogs]: ["", "#material"],
    [QUOTE_SETTINGS_PATHS.calibration]: [""],
    "/admin/users": ["", "#reviewer"],
  };
  return anchors[url.pathname]?.includes(url.hash) ? (href as string) : null;
}

/** Extra quote navigation is exact and read-only; the old settings enum stays separate. */
export function safeJourneyTarget(href: unknown): string | null {
  const old = safeSettingsJourneyTarget(href);
  if (old) return old;
  const url = localUrl(href);
  if (!url || url.hash) return null;
  if (
    url.pathname === "/app/quickquote" &&
    [...url.searchParams].length === 1 &&
    UUID.test(url.searchParams.get("draft_id") ?? "")
  )
    return href as string;
  if (
    /^\/app\/quickquote\/projects\//.test(url.pathname) &&
    !url.search &&
    UUID.test(url.pathname.split("/").at(-1) ?? "")
  )
    return href as string;
  return null;
}
export const journeyAtTarget = (href: string, origin: string): boolean =>
  !!safeJourneyTarget(href) &&
  canonicalQuoteOrigin(href) !== canonicalQuoteOrigin(origin);

function record(value: unknown): value is Record<string, unknown> {
  return (
    value !== null &&
    typeof value === "object" &&
    [Object.prototype, null].includes(Object.getPrototypeOf(value))
  );
}
function keys(value: Record<string, unknown>, allowed: string[]): boolean {
  return (
    Object.keys(value).length === allowed.length &&
    Object.keys(value).every((key) => allowed.includes(key))
  );
}
function validJson(value: unknown, seen: Set<object>, depth: number): boolean {
  if (depth > 24) return false;
  if (value === null || typeof value === "boolean" || typeof value === "string")
    return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "object" || seen.has(value)) return false;
  seen.add(value);
  const okay = Array.isArray(value)
    ? Array.from(value).every((v) => validJson(v, seen, depth + 1))
    : record(value) &&
      Object.keys(value).every(
        (key) =>
          !["__proto__", "prototype", "constructor"].includes(key) &&
          validJson(value[key], seen, depth + 1),
      );
  seen.delete(value);
  return okay;
}

export function isJourneySnapshot(
  value: unknown,
  scope: string,
  now: number,
): value is JourneySnapshot {
  try {
    if (
      !record(value) ||
      !keys(value, [
        "version",
        "id",
        "scope",
        "createdAt",
        "origin",
        "target",
        "pages",
      ]) ||
      value.version !== 1 ||
      typeof value.id !== "string" ||
      !UUID.test(value.id) ||
      value.scope !== scope ||
      !scope ||
      !Number.isSafeInteger(now) ||
      !Number.isSafeInteger(value.createdAt) ||
      typeof value.createdAt !== "number" ||
      value.createdAt <= 0 ||
      value.createdAt > now ||
      now - value.createdAt >= JOURNEY_TTL_MS ||
      !safeQuoteOrigin(value.origin) ||
      !safeJourneyTarget(value.target) ||
      !Array.isArray(value.pages) ||
      value.pages.length > PAGE_KEYS.length
    )
      return false;
    const pageKeys = new Set<string>();
    let bytes = 0;
    for (const page of value.pages) {
      if (
        !record(page) ||
        !keys(page, ["key", "version", "data", "files"]) ||
        typeof page.key !== "string" ||
        !PAGE_KEYS.includes(page.key as JourneyPageKey) ||
        pageKeys.has(page.key) ||
        page.version !== 1 ||
        !record(page.data) ||
        !validJson(page.data, new Set(), 0) ||
        !Array.isArray(page.files)
      )
        return false;
      pageKeys.add(page.key);
      bytes += new TextEncoder().encode(JSON.stringify(page.data)).byteLength;
      if (bytes > JOURNEY_JSON_LIMIT) return false;
      const slots = new Set<string>();
      for (const entry of page.files) {
        if (
          !record(entry) ||
          !keys(entry, ["slot", "file"]) ||
          typeof entry.slot !== "string" ||
          !/^[a-z][a-z0-9_-]{0,63}$/.test(entry.slot) ||
          slots.has(entry.slot) ||
          typeof File === "undefined" ||
          !(entry.file instanceof File)
        )
          return false;
        slots.add(entry.slot);
      }
    }
    return true;
  } catch {
    return false;
  }
}

export function parseJourneyPointer(
  value: unknown,
  scope: string,
): string | null {
  if (typeof value !== "string" || value.length > 2048) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return record(parsed) &&
      keys(parsed, ["version", "scope", "id"]) &&
      parsed.version === 1 &&
      parsed.scope === scope &&
      typeof parsed.id === "string" &&
      UUID.test(parsed.id)
      ? parsed.id
      : null;
  } catch {
    return null;
  }
}

export function stripJourneyState(
  state: unknown,
): Record<string, unknown> | null {
  if (state === null || typeof state !== "object" || Array.isArray(state))
    return null;
  const proto = Object.getPrototypeOf(state);
  if (proto !== Object.prototype && proto !== null) return null;
  return Object.fromEntries(
    Object.entries(state).filter(([key]) => key !== "quoteJourneyId"),
  );
}
